/**
 * SuggestionsDialog - Feature suggestion submission
 * 
 * Allows users to submit feature requests and suggestions.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Lightbulb as LightbulbIcon,
} from '@mui/icons-material';
import { telegram } from '../services/telegram';
import { feedbackService } from '../services/feedback';
import { useAutoDismiss } from '../hooks/useAutoDismiss';
import { useTranslation } from '../i18n/translations';

interface SuggestionsDialogProps {
  open: boolean;
  onClose: () => void;
}

const SuggestionsDialog: React.FC<SuggestionsDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [category, setCategory] = useState<string>('feature');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const SUGGESTION_CATEGORIES = [
    { id: 'feature', label: t('newFeature'), color: '#06b6d4' },
    { id: 'improvement', label: t('improvement'), color: '#10b981' },
    { id: 'bug', label: t('bugReport'), color: '#ef4444' },
    { id: 'navigation', label: t('navigation'), color: '#8b5cf6' },
    { id: 'map', label: t('mapMarkers'), color: '#f59e0b' },
    { id: 'other', label: t('other'), color: '#6b7280' },
  ];

  useAutoDismiss(error, () => setError(null));

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      setError(t('fillBothFields'));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Submit suggestion to API
      const response = await fetch('/api/suggestions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category,
          title: title.trim(),
          description: description.trim(),
          userId: telegram.getUserId(),
          userName: telegram.getUser()?.first_name || 'Anonymous',
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit suggestion');
      }

      setSubmitted(true);
      feedbackService.playSound('success');
      telegram.hapticFeedback('success');

      // Reset form after delay
      setTimeout(() => {
        setTitle('');
        setDescription('');
        setCategory('feature');
        setSubmitted(false);
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Failed to submit suggestion:', err);
      // Store locally if API fails
      const suggestions = JSON.parse(localStorage.getItem('pending_suggestions') || '[]');
      suggestions.push({
        category,
        title: title.trim(),
        description: description.trim(),
        timestamp: new Date().toISOString(),
      });
      localStorage.setItem('pending_suggestions', JSON.stringify(suggestions));
      
      setSubmitted(true);
      feedbackService.playSound('success');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setTitle('');
      setDescription('');
      setCategory('feature');
      setError(null);
      setSubmitted(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, rgba(18, 18, 18, 0.98) 0%, rgba(30, 30, 30, 0.98) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(6, 182, 212, 0.3)',
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LightbulbIcon sx={{ color: '#fbbf24' }} />
          <Typography
            variant="h6"
            sx={{
              background: 'linear-gradient(90deg, #06b6d4, #fbbf24)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 700,
            }}
          >
            {t('suggestFeature')}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={submitting}
          sx={{ color: 'rgba(255, 255, 255, 0.7)', '&:hover': { color: 'cyan' } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {submitted ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 4,
              animation: 'fadeIn 0.3s ease-out',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'scale(0.9)' },
                to: { opacity: 1, transform: 'scale(1)' },
              },
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(5, 150, 105, 0.3))',
                border: '3px solid #10b981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <SendIcon sx={{ fontSize: 40, color: '#10b981' }} />
            </Box>
            <Typography variant="h6" sx={{ color: '#10b981', fontWeight: 600, mb: 1 }}>
              {t('thankYou')}
            </Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center' }}>
              {t('suggestionSubmitted')}
            </Typography>
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Category Selection */}
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1, fontSize: '0.9rem' }}>
              {t('category')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {SUGGESTION_CATEGORIES.map((cat) => (
                <Chip
                  key={cat.id}
                  label={cat.label}
                  onClick={() => {
                    setCategory(cat.id);
                    feedbackService.tap();
                  }}
                  sx={{
                    background: category === cat.id 
                      ? `${cat.color}30`
                      : 'rgba(255, 255, 255, 0.05)',
                    border: `2px solid ${category === cat.id ? cat.color : 'transparent'}`,
                    color: category === cat.id ? cat.color : 'rgba(255, 255, 255, 0.6)',
                    fontWeight: category === cat.id ? 600 : 400,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: `${cat.color}20`,
                    },
                  }}
                />
              ))}
            </Box>

            {/* Title Input */}
            <TextField
              fullWidth
              label={t('title')}
              placeholder={t('titlePlaceholder')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={submitting}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: '#06b6d4' },
                },
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.5)' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#06b6d4' },
              }}
            />

            {/* Description Input */}
            <TextField
              fullWidth
              multiline
              rows={4}
              label={t('suggestionDescription')}
              placeholder={t('suggestionDescriptionPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={submitting}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: '#06b6d4' },
                },
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.5)' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#06b6d4' },
              }}
            />

            {/* Submit Button */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={<SendIcon />}
              onClick={handleSubmit}
              disabled={submitting || !title.trim() || !description.trim()}
              sx={{
                py: 1.5,
                background: 'linear-gradient(135deg, #06b6d4, #0891b2)',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, #0891b2, #0e7490)',
                },
                '&:disabled': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              {submitting ? t('submitting') : t('submitSuggestion')}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SuggestionsDialog;
